import { PartnerProps } from '@/models/Partner';
import { ProgramNames } from '@/models/Program';
import { Translations } from '@/utils/translations/Translations';

export const IntakeLabelsByProgram = (
  selectedProgramId: ProgramNames,
  partnerTerm: string,
  translatedText: Translations
) => {
  const intakeLabels = baseIntakeLabels(partnerTerm, translatedText);

  const programLabelGenerators: Record<
    ProgramNames,
    () => { [key in keyof PartnerProps]: string }
  > = {
    [ProgramNames.TreeCityUSA]: () => tcusaIntakeLabels(),
    [ProgramNames.TreeCitiesWorld]: () => tcowIntakeLabels(translatedText),
    [ProgramNames.TreeLineUSA]: () => tlineIntakeLabels(),
    [ProgramNames.TreeCampusHigherEducation]: () => tchigheredIntakeLabels(),
    [ProgramNames.TreeCampusHealthcare]: () => tchealthcareIntakeLabels(),
    [ProgramNames.TreeCampusK12]: () => tck12IntakeLabels(),
    [ProgramNames._]: function (): { [key in keyof PartnerProps]: string } {
      throw new Error('Function not implemented.');
    },
  };

  const programSpecificLabels = programLabelGenerators[selectedProgramId]?.() || {};

  return { ...intakeLabels, ...programSpecificLabels };
};

export const requiredFieldsByProgram = (programId: ProgramNames): Array<keyof PartnerProps> => {
  switch (programId) {
    case ProgramNames.TreeCityUSA:
      return tcusaRequiredFields;
    case ProgramNames.TreeCitiesWorld:
      return tcowRequiredFields;
    case ProgramNames.TreeLineUSA:
      return treeLineRequiredFields;
    case ProgramNames.TreeCampusHigherEducation:
      return treeCampusHigherEdRequiredFields;
    case ProgramNames.TreeCampusHealthcare:
      return treeCampusHealthcareRequiredFields;
    case ProgramNames.TreeCampusK12:
      return treeCampusK12RequiredFields;
    default:
      return baseRequiredFields;
  }
};

const baseRequiredFields = ['partnerName'] satisfies Array<keyof PartnerProps>;

const tcusaRequiredFields = [
  ...baseRequiredFields,
  'partnerSubCategoryId',
  'partnerSubCategoryOther',
] satisfies Array<keyof PartnerProps>;

const tcowRequiredFields = [
  ...baseRequiredFields,
  'partnerSubCategoryId',
  'partnerSubCategoryOther',
] satisfies Array<keyof PartnerProps>;

const treeLineRequiredFields = [...baseRequiredFields] satisfies Array<keyof PartnerProps>;

const treeCampusHigherEdRequiredFields = [...baseRequiredFields] satisfies Array<
  keyof PartnerProps
>;

const treeCampusHealthcareRequiredFields = [...baseRequiredFields] satisfies Array<
  keyof PartnerProps
>;

const treeCampusK12RequiredFields = [
  ...baseRequiredFields,
  'partnerSubCategoryId',
  'partnerSubCategoryOther',
  'schoolDistrict',
  'lowestGrade',
  'highestGrade',
] satisfies Array<keyof PartnerProps>;

function baseIntakeLabels(partnerTerm: string, translatedText: Translations) {
  return {
    partnerId: `${translatedText.signup.partnerSelect}${partnerTerm}`,
    partnerName: `${partnerTerm} ${translatedText.signup.labelName}`,
    partnerSubCategoryId: `${translatedText.signup.labelCategory}${partnerTerm}?`,
  };
}

function tcusaIntakeLabels() {
  return {
    partnerSubCategoryOther: 'Please specify',
  };
}

function tcowIntakeLabels(translatedText: Translations): {
  [key in keyof PartnerProps]: string;
} {
  return {
    partnerSubCategoryOther: translatedText.signup.labelSubCategoryOther,
  };
}

function tlineIntakeLabels() {
  return {}; // only uses base labels
}

function tchigheredIntakeLabels() {
  return {
    partnerName: `School Name`,
  };
}

function tchealthcareIntakeLabels() {
  return {
    healthcareSystem: 'Healthcare System Name',
  }; // only uses base labels
}

function tck12IntakeLabels() {
  return {
    partnerSubCategoryId: 'Additional School Classification(s)?',
    partnerName: 'School Name',
    genericEmail: 'Generic Email Address for City Government',
    schoolDistrict: 'School District Name',
    lowestGrade: 'Lowest Grade',
    highestGrade: 'Highest Grade',
  };
}
